import React, { useEffect, useMemo, useRef, useState } from 'react';
import Chips from '../Chips/index';
import { icons } from '@assets/icons/index';
import ImageComponent from '@sharedComponent/image-component';
import Action from '@sharedComponent/app-action-reducer/action';
import { images } from 'src/assets/img';
import './side-navbar.scss';
import { useHistory, useLocation } from 'react-router-dom';
import { APP_VARIABLES, PENDING_ALERTS_CLAIMS, SETUP_REDIRECT } from '@constants/app-constants';
import userManager from 'src/config/user-manager';
import { arrayObjToArray, syncLocalStorage, getLocalvariable, removeLocalvariable } from '@helpers';
import { useDispatch, useSelector } from 'react-redux';
import useSideEffects from './hooks/useSideEffects';
import useMenus from './hooks/useMenus';
import ClevertapReact from 'src/utils/clever-tap';
import { mergeAllClaims } from 'src/utils/claims';
import { LOCAL_STORAGE_KEYS } from '@constants/localStorage';
import { MFA_CONSTANTS } from '@pages/auth/mfa/constants';

function useOutsideAlerter(ref, isToggledToMin, setMainMenu) {
	const _isToggledToMin = useRef(isToggledToMin);

	useEffect(() => {
		_isToggledToMin.current = isToggledToMin;
	}, [isToggledToMin]);

	useEffect(() => {
		/**
		 * Alert if clicked on outside of element
		 */
		function handleClickOutside(event) {
			if (ref.current && !ref.current.contains(event.target)) {
				if (_isToggledToMin.current) {
					setMainMenu((prevState) => {
						const result = prevState?.map((item, index) => {
							return { ...item, isVisible: false };
						});
						return result || [];
					});
				}
			}
		}
		// Bind the event listener
		document.addEventListener('mousedown', handleClickOutside);
		return () => {
			// Unbind the event listener on clean up
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, [ref]);
}

const SideNavBar = ({ removeUser, currentUser }) => {
	const [mainMenu, setMainMenu] = useState([]);
	const [navbar, setNavbar] = useState(false);
	const [payrollNewNavBarState, setPayrollNewNavBarState] = useState(false); //this use state is used to handle the navbar while the user enter to payroll-plus
	const [size, setSize] = useState(false);
	const approveReqCount = useSelector((state) => state?.sharedStates?.PendingStatusDetail?.pendingRequest);
	const role = (currentUser && currentUser?.roles && currentUser?.roles) || null;
	const emp_Id = useSelector((state) => state?.sharedStates?.currentUser?.user?.employeeCode);
	const profileImagePath = useSelector((state) => state?.sharedStates?.currentUser?.user?.profileImagePath);
	const profileImg = useSelector((state) => state?.sharedStates?.profileImg);
	const profileImgLoading = useSelector((state) => state?.sharedStates?.profileImgLoading);
	const isOnlyEmployeeExits = useSelector((state) => state?.sharedStates?.isOnlyEmployeeExits) || false;
	const userName = currentUser?.user?.userName;
	const location = useLocation();
	const history = useHistory();
	const allClaims = useMemo(() => mergeAllClaims(currentUser), [currentUser]);
	const { menus } = useMenus();
	const isToggledToMin = navbar || payrollNewNavBarState || size;
	useSideEffects({ setMainMenu, menus, setSize, location, mainMenu });
	const dispatch = useDispatch();

	const navBarRef = useRef();
	useOutsideAlerter(navBarRef, isToggledToMin, setMainMenu);

	useEffect(() => {
		if (profileImagePath) {
			dispatch(Action.creators.getProfileImageStart(profileImagePath));
		}
	}, [profileImagePath]);
	const navBarStatusForPages = [
		'/payroll-plus',
		'/uploadSpreadsheetPlus',
		'/hr-features/allowance',
		'/hr-features/addition',
		'/hr-features/deduction',
		'/uploadSpreadsheetSetup',
		'/payroll-plus/preview-uploaded-document-data',
		'/run-payroll',
	];

	useEffect(() => {
		if (navBarStatusForPages.some((pageName) => pageName === location?.pathname)) {
			setPayrollNewNavBarState(true);
		} else {
			setPayrollNewNavBarState(false);
		}
	}, [location?.pathname]);

	const handleLogout = () => {
		ClevertapReact.event('Sidebar_Logout_Clicked', { user_id: currentUser.userId });
		removeUser();
		localStorage.removeItem(APP_VARIABLES.USER_DETAILS);
		userManager.signoutRedirect({ id_token_hint: localStorage.getItem(APP_VARIABLES.ID_TOKEN) });
		userManager.removeUser(); // removes the user data from sessionStorage
		localStorage.removeItem(APP_VARIABLES.ACCESS_TOKEN);
		localStorage.removeItem(APP_VARIABLES.CORPORATE_ID);
		localStorage.removeItem(APP_VARIABLES.CORPORATE_NAME);
		localStorage.removeItem('isRedirectNeeded');
		localStorage.removeItem('isRedirectVerifierNeeded');
		localStorage.removeItem('amount');
		localStorage.removeItem('transferFundsPopupVisible');
		localStorage.removeItem('showUnemploymentBanner');
		localStorage.removeItem(SETUP_REDIRECT.SETUP_REDIRECT_TO);
		// removing the iloe banner keys
		syncLocalStorage.removeItem(LOCAL_STORAGE_KEYS.iloe.dashboardRenewalBanner)
		syncLocalStorage.removeItem(LOCAL_STORAGE_KEYS.iloe.beforeRenewal)
		syncLocalStorage.removeItem(LOCAL_STORAGE_KEYS.iloe.afterRenewal)
		// While setup mfa and update mobile number page storing countries api data in local storage and removing it while logout if it is unused.
		if (getLocalvariable(MFA_CONSTANTS.localStorage.countriesKey)) {
			removeLocalvariable(MFA_CONSTANTS.localStorage.countriesKey)
		}
	}

	const onMainMenuClick = (menuKey) => {
		let pathToPush = null;
		let cleverTapValue = null;
		const head = mainMenu?.map((item) => {
			if (item.key === menuKey) {
				pathToPush = typeof item.path === 'function' ? item.path(item.subMenus) : item.path;
				cleverTapValue = item.cleverTapValue;
				return {
					...item,
					isSelect: !item.isSelect,
					isVisible: !item.isVisible,
					subMenus: item.subMenus
						? item.subMenus.map((subMenu, index) => {
							if (index === 0) {
								return {
									...subMenu,
									isSelect: true,
								};
							} else {
								return { ...subMenu, isSelect: false };
							}
						})
						: [],
				};
			}
			return { ...item, isSelect: false };
		});
		setMainMenu(head || []);
		ClevertapReact.event(cleverTapValue);
		if (menuKey === 'referAClient') {
			window.open(pathToPush, '_blank', 'noopener,noreferrer');
			return;
		}
		if (typeof pathToPush === 'string') {
			history.push({ pathname: pathToPush });
		}
		if (menuKey === 'features') {
			ClevertapReact.event('navbar_newfeatures');
		}
	};

	const onSubMenuClick = (menuKey) => {
		let pathToPush = null;
		let cleverTapValue = null;
		const submenus = mainMenu?.map((item) => {
			let menusIsSelected = false;
			return {
				...item,
				subMenus: item?.subMenus?.map((items) => {
					if (items.key === menuKey) {
						pathToPush = typeof items.path === 'function' ? items.path() : items.path;
						menusIsSelected = true;
						cleverTapValue = items?.cleverTapValue;
						return { ...items, isSelect: true };
					}
					return { ...items, isSelect: false };
				}),
				isSelect: menusIsSelected,
			};
		});
		setMainMenu(submenus);
		if (typeof pathToPush === 'string') {
			history.push({ pathname: pathToPush });
		}
		if (typeof cleverTapValue === 'string') {
			ClevertapReact.event(cleverTapValue, null);
		}
	};
	const { showInvoiceMenuFlag } = useSelector((state) => state?.sharedStates);

	const menuFilter = (array, role) => {
		return array?.filter((items) => {
			if (items.key === 'invoice') {
				return items.isNavigation === role && showInvoiceMenuFlag;
			} else {
				return items.isNavigation === role;
			}
		});
	};

	const payrollMenuItems = menuFilter(mainMenu, 'payroll');
	const hrMenuItems = menuFilter(mainMenu, 'hr');
	const defaultMenuItems = menuFilter(mainMenu, 'default');
	const bottomMenuItems = menuFilter(mainMenu, 'bottom');

	const shouldShowVerifyApproval = () => {
		const shouldShow = allClaims.some((userClaim) => {
			return PENDING_ALERTS_CLAIMS.indexOf(userClaim) >= 0;
		});
		return shouldShow;
	};

	return (
		<section className={navbar || payrollNewNavBarState || size ? 'responsive' : 'navbar'} ref={navBarRef}>
			<div className="side-nav-outer">
				<div className="side-nav-inner">
					<div className="side-nav-inner__top">
						<div className="profile">
							<div
								className="profile-image"
								onClick={() => {
									ClevertapReact.event('Sidebar_Profile_Section_Clicked');
									history.push({ pathname: '/dashboard/profile', state: { tableData: { employeeId: emp_Id }, mainProfile: true } })
								}}>
								<ImageComponent
									style={{ height: '30px', width: '30px', borderRadius: '10%', objectFit: 'cover', backgroundColor: '#ddd' }}
									src={profileImg ? profileImg : icons.focus_state.User}
									className={`user ${profileImgLoading ? 'imgLoading' : ''}`}
								/>
							</div>
							<div
								className="profile-content"
								onClick={() =>
									history.push({ pathname: '/dashboard/profile', state: { tableData: { employeeId: emp_Id }, mainProfile: true } })
								}>
								<p title={userName ? userName : ''} className="h5 side-nav-user-name">
									{userName ? userName : ''}
								</p>
								<p className="verifier">
									{isOnlyEmployeeExits
										? 'Account'
										: arrayObjToArray(role, 'name')?.includes('SuperAdmin')
											? 'Super Admin'
											: 'Admin'}
								</p>
							</div>

							<div className="profile-icon" style={{ paddingLeft: '10px' }}>
								<ImageComponent
									src={images.menu}
									onClick={() => {
										const navbarStatus = navbar ? 'From Retracted to Expanded' : 'From Expanded to Retracted';
										ClevertapReact.event('Sidebar_Clicked', { status: navbarStatus });
										if (navBarStatusForPages.some((pageName) => pageName === location?.pathname)) {
											setPayrollNewNavBarState(!payrollNewNavBarState);
										} else {
											setNavbar(!navbar);
										}
									}}
									className="navicon"
								/>
							</div>
						</div>
						{shouldShowVerifyApproval() === true && (
							<div className="nav-item pending-outer">
								<div
									className="pending"
									onClick={() => {
										ClevertapReact.event('Sidebar_Pending_Alerts_Clicked', { pending_alerts_count: approveReqCount?.totalRequest });
										history.push({ pathname: '/dashboard/pending-alerts', state: { approveReqCount, role } });
									}}>
									<div className="image">
										<div className="pending-image">
											<ImageComponent src={images.pending} />
										</div>
									</div>
									<div className="pending-content">
										<p className="alert">
											{approveReqCount && approveReqCount?.totalRequest ? approveReqCount?.totalRequest : 0} Pending Alerts
										</p>
										<p className="details">View Details</p>
									</div>
									<div className="pending-arrow">
										<ImageComponent src={images.pendingIcon} className="pending-arrow-image" />
									</div>
								</div>
							</div>
						)}
					</div>
					<div className="side-nav-inner__main">
						{payrollMenuItems?.map((item, index) => (
							<GetMenuItem
								onHover={navbar || payrollNewNavBarState ? true : false}
								key={index}
								menus={item}
								onMainMenuClick={onMainMenuClick}
								mainMenu={mainMenu}
								onSubMenuClick={onSubMenuClick}
							/>
						))}
						{hrMenuItems?.map((item, index) => (
							<GetMenuItem
								onHover={navbar || payrollNewNavBarState ? true : false}
								key={index}
								menus={item}
								onMainMenuClick={onMainMenuClick}
								mainMenu={mainMenu}
								onSubMenuClick={onSubMenuClick}
							/>
						))}
						{defaultMenuItems?.map((item, index) => (
							<GetMenuItem
								onHover={navbar || payrollNewNavBarState ? true : false}
								key={index}
								menus={item}
								onMainMenuClick={onMainMenuClick}
								mainMenu={mainMenu}
								onSubMenuClick={onSubMenuClick}
							/>
						))}
					</div>
					<div className="side-nav-inner__bottom">
						{bottomMenuItems?.map((item, index) => (
							<GetMenuItem
								onHover={navbar || payrollNewNavBarState ? true : false}
								key={index}
								menus={item}
								onMainMenuClick={onMainMenuClick}
								mainMenu={mainMenu}
								onSubMenuClick={onSubMenuClick}
							/>
						))}
					</div>
					<div className="side-nav-inner__logout">
						<div className="nav-item" onClick={handleLogout}>
							<ImageComponent
								src={icons.not_focus.logoutnew}
								className="nav-item__icon"
							/>
							<div className="nav-item__label">Logout</div>
						</div>
					</div>
				</div>
			</div>
		</section>
	);
};

export default SideNavBar;

const GetMenuItem = ({ menus, onMainMenuClick, onSubMenuClick, onHover }) => {
	const location = useLocation();
	const payrollSubmenuPath = [
		'/payroll-plus',
		'/uploadSpreadsheetPlus',
		'/hr-features/allowance',
		'/hr-features/addition',
		'/hr-features/deduction',
		'/uploadSpreadsheetSetup',
		'/payroll-plus/preview-uploaded-document-data',
		'/run-payroll',
		'/salary',
	];
	const companySubmenuPath = ['/company/company-setting', '/manage_team', '/company/information'];
	const leavesSubmenuPath = ['/approval-workflow-leave'];
	const menuActiveState = (label, path) => {
		if (path === location?.pathname) {
			return true;
		} else if (
			label === 'Payroll' &&
			(location?.pathname.split('/')[1] === 'payroll' || payrollSubmenuPath.some((path) => path === location?.pathname))
		) {
			return true;
		} else if (label === 'Employees' && location?.pathname.split('/')[1] === 'employees') {
			return true;
		} else if (
			label === 'Leaves' &&
			(location?.pathname.split('/')[1] === 'leaves' || leavesSubmenuPath.some((path) => path === location?.pathname))
		) {
			return true;
		} else if (
			label === 'Company' &&
			(location?.pathname.split('/')[1] === 'company' || companySubmenuPath.some((path) => path === location?.pathname))
		) {
			return true;
		} else if (label === 'Unemployment Insurance' && location?.pathname.split('/')[1] === 'un-employment-insurance') {
			return true;
		} else {
			return false;
		}
	};

	if (menus?.type === 'common') {
		return <></>;
	} else {
		return (
			<div className={`${onHover ? 'hover' : 'hide-hover'}`}>
				<div
					className={`nav-item-outer ${menuActiveState(menus?.label, menus?.path) ? 'nav-item-outer_isActive' : ''} ${menus?.chip ? 'nav-item-outer_hasChip' : ''}`}
					onClick={() => onMainMenuClick(menus?.key)}
				>
					<div className={`nav-item ${menuActiveState(menus?.label, menus?.path) && 'nav-item_isActive'} `}>
						<ImageComponent
							src={menuActiveState(menus?.label, menus?.path) ? menus.activeIcon : menus.icon}
							className="nav-item__icon"
						/>
						<div className={`nav-item__label ${menuActiveState(menus?.label, menus?.path) && 'nav-item__label_isActive'}`}>{menus.label} </div>
					</div>
					<div className="nav-item-outer__chip">
						{menus?.chip && (
							<Chips color="#EEDCBA">
								<p className="text">New</p>
							</Chips>
						)}
					</div>
					<div className="nav-item-outer__arrow-container">
						{menus?.subMenus?.length > 0 && (
							<ImageComponent src={menus?.isVisible ? icons.not_focus.up : icons.not_focus.down} className="nav-item-outer__arrow" />
						)}
					</div>
				</div>
				<div className="submenu-outer">
					{menus?.subMenus?.length > 0 &&
						menus?.isVisible &&
						menus?.subMenus?.map((item) => <GetSubMenu menus={item} onSubMenuClick={onSubMenuClick} />)}
				</div>
			</div>
		);
	}
};
const GetSubMenu = ({ menus, onSubMenuClick }) => {
	return (
		<div className={`submenu ${menus.isSelect && 'submenu_isActive'}`} onClick={() => onSubMenuClick(menus?.key)}>
			<p className="submenu__label">{menus.label} </p>
		</div>
	);
};
